/**
 * Gate I8 Phase 3+4: Workflow Bucket Detail Component
 * 
 * Shows animals in a selected bucket with guidance title and checklist.
 * Items are pre-sorted by parent component based on user's sort preference.
 * 
 * Phase 4: Uses WorkflowAnimalTile component for animal display.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  TRACK_LABELS, 
  EVAL_STATUS_LABELS,
  type WorkflowDashboardItem,
  type BucketKey,
  type Track,
  type EvalStatus
} from "@/lib/api/workflow-types";
import { WorkflowAnimalTile } from "./WorkflowAnimalTile";

interface WorkflowBucketDetailProps {
  bucketKey: BucketKey;
  items: WorkflowDashboardItem[];
  highlightedAnimalId?: string | null;
}

function parseBucketKey(bucketKey: BucketKey): { track: Track; evalStatus: EvalStatus } {
  const [track, evalStatus] = bucketKey.split(':') as [Track, EvalStatus];
  return { track, evalStatus };
}

export function WorkflowBucketDetail({ bucketKey, items, highlightedAnimalId }: WorkflowBucketDetailProps) {
  const { track, evalStatus } = parseBucketKey(bucketKey);
  
  return (
    <Card data-testid="workflow-bucket-detail">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span>{TRACK_LABELS[track]}</span>
          <span className="text-muted-foreground">•</span>
          <span>{EVAL_STATUS_LABELS[evalStatus]}</span>
        </CardTitle>
        <CardDescription>
          {items.length} {items.length === 1 ? 'animal' : 'animals'} in this selection
        </CardDescription>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            No animals in this selection.
          </p>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <div
                key={item.animalId}
                id={`workflow-bucket-animal-${item.animalId}`}
                data-testid={highlightedAnimalId === item.animalId ? 'workflow-search-highlighted' : undefined}
              >
                <WorkflowAnimalTile 
                  item={item}
                  isHighlighted={highlightedAnimalId === item.animalId}
                />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default WorkflowBucketDetail;
